<template>
    <el-dialog v-model="vdata.visible" title="商户数据调整" width="700px" destroy-on-close>

        <div
            style="background-color: #fafafa; padding: 16px 24px; margin-bottom: 24px; border-radius: 4px; border: 1px solid #f0f0f0;">
            <el-row>
                <el-col :span="24">
                    <span style="color: #888">商户名称：</span>
                    <span style="font-weight: 500; color: #333">{{ vdata.formData.mchName }}</span>
                </el-col>
            </el-row>
        </div>

        <el-form :model="vdata.formData" label-width="130px">
            <el-divider content-position="left">实收金额调整</el-divider>
            <el-form-item label="调整操作：">
                <el-radio-group v-model="vdata.amountType" @change="onAmountTypeChange">
                    <el-radio :label="null">暂不调整</el-radio>
                    <el-radio label="add">实际收款增加</el-radio>
                    <el-radio label="sub">实际收款减少</el-radio>
                </el-radio-group>
            </el-form-item>
            <el-form-item label="增加金额(元)：" v-if="vdata.amountType === 'add'">
                <el-input-number v-model="vdata.formData.amountAdd" :min="0" :precision="2" placeholder="填入实际收款增加金额"
                    style="width: 240px;" />
            </el-form-item>
            <el-form-item label="减少金额(元)：" v-if="vdata.amountType === 'sub'">
                <el-input-number v-model="vdata.formData.amountSub" :min="0" :precision="2" placeholder="填入实际收款减少金额"
                    style="width: 240px;" />
            </el-form-item>

            <el-divider content-position="left">订单笔数调整</el-divider>
            <el-form-item label="调整操作：">
                <el-radio-group v-model="vdata.countType" @change="onCountTypeChange">
                    <el-radio :label="null">暂不调整</el-radio>
                    <el-radio label="add">订单笔数增加</el-radio>
                    <el-radio label="sub">订单笔数减少</el-radio>
                </el-radio-group>
            </el-form-item>
            <el-form-item label="增加笔数：" v-if="vdata.countType === 'add'">
                <el-input-number v-model="vdata.formData.countAdd" :min="0" :precision="0" placeholder="填入增加的订单笔数"
                    style="width: 240px;" />
            </el-form-item>
            <el-form-item label="减少笔数：" v-if="vdata.countType === 'sub'">
                <el-input-number v-model="vdata.formData.countSub" :min="0" :precision="0" placeholder="填入减少的订单笔数"
                    style="width: 240px;" />
            </el-form-item>

            <el-divider content-position="left">操作日期</el-divider>
            <el-form-item label="选择操作日期：" required>
                <el-date-picker v-model="vdata.formData.offset_date" type="date" value-format="YYYY-MM-DD"
                    placeholder="请选择操作日期" style="width: 240px;" />
            </el-form-item>
        </el-form>

        <template #footer>
            <span class="dialog-footer">
                <el-button @click="vdata.visible = false">取消</el-button>
                <el-button type="primary" :loading="vdata.confirmLoading" @click="handleOk">
                    确定
                </el-button>
            </span>
        </template>
    </el-dialog>
</template>

<script setup>
/* global defineExpose, defineEmits */
import { reactive, getCurrentInstance } from 'vue'
import { ElMessage } from 'element-plus'

const { appContext } = getCurrentInstance()
const emit = defineEmits(['refresh'])

const vdata = reactive({
    visible: false,
    confirmLoading: false,
    amountType: null, // 控制金额单选状态
    countType: null,  // 控制笔数单选状态
    formData: {
        mch_no: '',
        mchName: '',
        amountAdd: null,
        amountSub: null,
        countAdd: null,
        countSub: null,
        offset_date: null
    }
})

// 监听操作切换，清空旧数据
function onAmountTypeChange() {
    vdata.formData.amountAdd = null
    vdata.formData.amountSub = null
}
function onCountTypeChange() {
    vdata.formData.countAdd = null
    vdata.formData.countSub = null
}

// 父组件调用的显示弹窗方法
function show(record) {
    // 拦截：判断商户是否已经授权修改偏移量
    if (record.enable_stat_offset !== 1) {
        return ElMessage.warning('该商户尚未开启数据调整授权，请联系运营开启！')
    }

    // 获取当天的 YYYY-MM-DD 格式作为默认日期
    const today = new Date()
    const defaultDate = today.getFullYear() + '-' +
        String(today.getMonth() + 1).padStart(2, '0') + '-' +
        String(today.getDate()).padStart(2, '0')

    // 每次打开清空并初始化数据
    vdata.amountType = null
    vdata.countType = null
    vdata.formData = {
        mch_no: record.no, // 列表里的商户号字段是 no
        mchName: record.merchant_alias,
        amountAdd: null,
        amountSub: null,
        countAdd: null,
        countSub: null,
        offset_date: defaultDate
    }
    vdata.visible = true
}

// 确认提交
function handleOk() {
    if (!vdata.formData.offset_date) {
        return ElMessage.error('请选择操作日期')
    }

    // 金额偏移量计算：区分正负并转换为“分”单位
    let realAmountOffset = 0
    if (vdata.amountType === 'add') {
        if (vdata.formData.amountAdd === null || vdata.formData.amountAdd === undefined) return ElMessage.error('请填写实际收款增加金额')
        realAmountOffset = Math.round(vdata.formData.amountAdd * 100)
    } else if (vdata.amountType === 'sub') {
        if (vdata.formData.amountSub === null || vdata.formData.amountSub === undefined) return ElMessage.error('请填写实际收款减少金额')
        realAmountOffset = -Math.round(vdata.formData.amountSub * 100)
    }

    // 笔数偏移量计算：区分正负
    let orderCountOffset = 0
    if (vdata.countType === 'add') {
        if (vdata.formData.countAdd === null || vdata.formData.countAdd === undefined) return ElMessage.error('请填写增加的订单笔数')
        orderCountOffset = vdata.formData.countAdd
    } else if (vdata.countType === 'sub') {
        if (vdata.formData.countSub === null || vdata.formData.countSub === undefined) return ElMessage.error('请填写减少的订单笔数')
        orderCountOffset = -vdata.formData.countSub
    }

    vdata.confirmLoading = true

    // 组装参数
    const reqData = {
        mch_no: vdata.formData.mch_no,
        order_count_offset: orderCountOffset,
        real_amount_offset: realAmountOffset,
        offset_date: vdata.formData.offset_date
    }

    // 调用全局挂载的 api (确保你在 api 配置文件里写了 setStatOffset)
    appContext.config.globalProperties.$api.merchant.setStatOffset(reqData).then((res) => {
        if (res.status == 200) {
            ElMessage.success('数据调整成功')
            vdata.visible = false
            emit('refresh') // 触发父组件的刷新列表方法
        } else {
            ElMessage.error(res.message || '调整失败')
        }
    }).catch((err) => {
        console.error(err)
        ElMessage.error('接口请求异常')
    }).finally(() => {
        vdata.confirmLoading = false
    })
}

// 将 show 方法暴露给父组件调用
defineExpose({ show })
</script>